import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axiosInstance from "../../api/axiosInstance";

const TOAST_DURATION = 4000;
const PAGE_SIZE = 9;

// ── API layer (axiosInstance — token + 401 auto-refresh handled automatically) ─
const tcApi = {
  list: (vendorProfileId, params = {}) => {
    const p = {};
    if (params.search)   p.q         = params.search;
    if (params.category) p.category  = params.category;
    if (params.isActive !== "" && params.isActive !== undefined)
                         p.is_active = params.isActive;
    return axiosInstance.get(
      `/api/superadmin/vendors/${vendorProfileId}/terms-conditions/`,
      { params: p }
    );
  },
  get: (vendorProfileId, id) =>
    axiosInstance.get(`/api/superadmin/vendors/${vendorProfileId}/terms-conditions/${id}/`),
  create: (vendorProfileId, body) =>
    axiosInstance.post(`/api/superadmin/vendors/${vendorProfileId}/terms-conditions/`, body),
  patch: (vendorProfileId, id, body) =>
    axiosInstance.patch(`/api/superadmin/vendors/${vendorProfileId}/terms-conditions/${id}/`, body),
  delete: (vendorProfileId, id) =>
    axiosInstance.delete(`/api/superadmin/vendors/${vendorProfileId}/terms-conditions/${id}/`),
};

const CATEGORIES = [
  { value: "payment",    label: "Payment",    color: "#2563eb", bg: "#dbeafe", border: "#bfdbfe" },
  { value: "pricing",    label: "Pricing",    color: "#7c3aed", bg: "#ede9fe", border: "#ddd6fe" },
  { value: "delivery",   label: "Delivery",   color: "#059669", bg: "#d1fae5", border: "#a7f3d0" },
  { value: "warranty",   label: "Warranty",   color: "#d97706", bg: "#fef3c7", border: "#fde68a" },
  { value: "compliance", label: "Compliance", color: "#0891b2", bg: "#cffafe", border: "#a5f3fc" },
  { value: "penalty",    label: "Penalty",    color: "#dc2626", bg: "#fee2e2", border: "#fecaca" },
  { value: "general",    label: "General",    color: "#475569", bg: "#f1f5f9", border: "#e2e8f0" },
  { value: "other",      label: "Other",      color: "#64748b", bg: "#f8fafc", border: "#e2e8f0" },
];
const getCatConfig = (key) =>
  CATEGORIES.find((c) => c.value === key) || {
    value: key, label: key, color: "#64748b", bg: "#f8fafc", border: "#e2e8f0",
  };

let _tcToastSetter = null;
const toast = {
  _push: (item) => {
    const id = Date.now() + Math.random();
    _tcToastSetter?.((p) => [...p, { id, ...item }]);
    return id;
  },
  success: (title, msg) => toast._push({ type: "success", title, msg }),
  error:   (title, msg) => toast._push({ type: "error",   title, msg }),
  info:    (title, msg) => toast._push({ type: "info",    title, msg }),
  loading: (title)      => toast._push({ type: "loading", title, persistent: true }),
  dismiss: (id)         => _tcToastSetter?.((p) => p.filter((t) => t.id !== id)),
};
const TOAST_META = {
  success: { bar: "#16a34a", icon: "✓" },
  error:   { bar: "#dc2626", icon: "✕" },
  info:    { bar: "#1d4ed8", icon: "i" },
  loading: { bar: "#94a3b8", icon: null },
};
const CloseIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const ToastItem = ({ t, onDismiss }) => {
  const meta = TOAST_META[t.type];
  const [progress, setProgress] = useState(100);
  const [exiting, setExiting]   = useState(false);
  const rafRef  = useRef(null);
  const startTs = useRef(Date.now());
  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(t.id), 280);
  }, [t.id, onDismiss]);
  useEffect(() => {
    if (t.persistent) return;
    const tick = () => {
      const pct = Math.max(0, 100 - ((Date.now() - startTs.current) / TOAST_DURATION) * 100);
      setProgress(pct);
      if (pct > 0) rafRef.current = requestAnimationFrame(tick);
      else dismiss();
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [t.persistent, dismiss]);
  return (
    <div style={{ position: "relative", overflow: "hidden", background: "#fff", borderRadius: 10, borderLeft: `4px solid ${meta.bar}`, boxShadow: "0 4px 20px rgba(0,0,0,.10), 0 0 0 1px rgba(0,0,0,.06)", minWidth: 300, maxWidth: 360, animation: exiting ? "tc-alOut .28s ease forwards" : "tc-alIn .28s cubic-bezier(.34,1.56,.64,1) forwards" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "13px 14px 14px" }}>
        <div style={{ flexShrink: 0, width: 24, height: 24, borderRadius: "50%", background: `${meta.bar}18`, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
          {t.type === "loading"
            ? <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ animation: "tc-spin .85s linear infinite" }}><circle cx="6.5" cy="6.5" r="5" stroke={meta.bar} strokeWidth="2" strokeDasharray="8 20" strokeLinecap="round" /></svg>
            : <span style={{ fontSize: 11, fontWeight: 800, color: meta.bar, lineHeight: 1 }}>{meta.icon}</span>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13.5, fontWeight: 650, color: "#0f172a", lineHeight: 1.35 }}>{t.title}</p>
          {t.msg && <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "#64748b", lineHeight: 1.5 }}>{t.msg}</p>}
        </div>
        <button onClick={dismiss} style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", padding: 2, flexShrink: 0, display: "flex", borderRadius: 4 }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#94a3b8")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#cbd5e1")}>
          <CloseIcon />
        </button>
      </div>
      {!t.persistent && (
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "#f1f5f9" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: meta.bar, transition: "width .1s linear", borderRadius: "0 2px 0 0" }} />
        </div>
      )}
    </div>
  );
};
const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  _tcToastSetter = setToasts;
  const dismiss = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);
  if (!toasts.length) return null;
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-end" }}>
      {toasts.map((t) => <ToastItem key={t.id} t={t} onDismiss={dismiss} />)}
    </div>
  );
};

const LoaderIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    style={{ animation: "tc-spin .8s linear infinite", flexShrink: 0 }}>
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);
const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const RefreshIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const EditIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);
const EyeIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const FileTextIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
  </svg>
);
const TagIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);
const UserIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const SlugIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);
const AlertTriangleIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const SkeletonBlock = ({ w, h = 14, r = 6 }) => (
  <div style={{ height: h, width: w, borderRadius: r, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "tc-shimmer 1.4s infinite" }} />
);

const inputBase = (hasError = false) => ({
  padding: "9px 12px",
  border: `1px solid ${hasError ? "#fca5a5" : "#e2e8f0"}`,
  borderRadius: 8,
  fontSize: 13.5,
  background: hasError ? "#fff5f5" : "#fff",
  color: "#1e293b",
  outline: "none",
  fontFamily: "inherit",
  width: "100%",
  boxSizing: "border-box",
  transition: "border-color .15s, background .15s",
});

const CategoryBadge = ({ category, label }) => {
  const c = getCatConfig(category);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 700, background: c.bg, color: c.color, border: `1px solid ${c.border}`, whiteSpace: "nowrap" }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: c.color, display: "inline-block", flexShrink: 0 }} />
      {label || c.label}
    </span>
  );
};

const ActiveBadge = ({ value }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 700, background: value ? "#dcfce7" : "#fee2e2", color: value ? "#16a34a" : "#dc2626", border: `1px solid ${value ? "#bbf7d0" : "#fecaca"}` }}>
    <span style={{ width: 5, height: 5, borderRadius: "50%", background: value ? "#22c55e" : "#f43f5e", display: "inline-block" }} />
    {value ? "Active" : "Inactive"}
  </span>
);

const InfoRow = ({ icon, label, value, mono, badge }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "7px 0", borderBottom: "1px solid #f8fafc" }}>
    <span style={{ color: "#94a3b8", display: "flex", flexShrink: 0, marginTop: 1 }}>{icon}</span>
    <span style={{ fontSize: 12.5, color: "#94a3b8", fontWeight: 550, minWidth: 130, flexShrink: 0 }}>{label}</span>
    {badge || (
      <span style={{ fontSize: 13, color: mono ? "#1e40af" : "#1e293b", fontWeight: mono ? 700 : 500, fontFamily: mono ? "monospace" : "inherit", flex: 1, wordBreak: "break-all" }}>
        {value ?? <span style={{ color: "#cbd5e1" }}>—</span>}
      </span>
    )}
  </div>
);

const Pagination = ({ currentPage, totalPages, totalCount, onPageChange }) => {
  if (totalPages <= 1) return null;
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem   = Math.min(currentPage * PAGE_SIZE, totalCount);
  const getPages  = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [1];
    if (currentPage > 3) pages.push("...");
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);
    return pages;
  };
  const btnBase = { display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 34, height: 34, padding: "0 6px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer", background: "#fff", color: "#374151", transition: "all .15s", lineHeight: 1 };
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 4px", borderTop: "1px solid #f1f5f9", flexWrap: "wrap", gap: 10 }}>
      <span style={{ fontSize: 13, color: "#64748b" }}>
        Showing <strong style={{ color: "#374151" }}>{startItem}–{endItem}</strong> of <strong style={{ color: "#374151" }}>{totalCount}</strong>
      </span>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
          style={{ ...btnBase, opacity: currentPage === 1 ? 0.4 : 1, cursor: currentPage === 1 ? "not-allowed" : "pointer" }}>
          <ChevronLeftIcon />
        </button>
        {getPages().map((p, i) =>
          p === "..."
            ? <span key={`e${i}`} style={{ padding: "0 4px", color: "#94a3b8", fontSize: 13, lineHeight: "34px" }}>…</span>
            : <button key={p} onClick={() => onPageChange(p)}
                style={{ ...btnBase, ...(p === currentPage ? { background: "#3b82f6", color: "#fff", border: "1px solid #3b82f6", fontWeight: 700 } : {}) }}
                onMouseEnter={(e) => { if (p !== currentPage) e.currentTarget.style.background = "#f1f5f9"; }}
                onMouseLeave={(e) => { if (p !== currentPage) e.currentTarget.style.background = "#fff"; }}>
                {p}
              </button>
        )}
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}
          style={{ ...btnBase, opacity: currentPage === totalPages ? 0.4 : 1, cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}>
          <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
};

const DeleteConfirmModal = ({ item, onConfirm, onCancel, deleting }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape" && !deleting) onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", onKey); };
  }, [onCancel, deleting]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.60)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200, padding: 20 }}
      onClick={() => { if (!deleting) onCancel(); }}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 420, padding: "28px", boxShadow: "0 24px 64px rgba(0,0,0,.22)", animation: "tc-fadeUp .2s ease" }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 22 }}>
          <div style={{ width: 46, height: 46, borderRadius: 12, background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "#dc2626" }}>
            <AlertTriangleIcon size={22} />
          </div>
          <div>
            <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#0f172a" }}>Delete Term & Condition</h3>
            <p style={{ margin: 0, fontSize: 13.5, color: "#64748b", lineHeight: 1.6 }}>
              Are you sure you want to delete{" "}
              <strong style={{ color: "#0f172a" }}>"{item?.title}"</strong>?{" "}
              This action cannot be undone.
            </p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onCancel} disabled={deleting}
            style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: deleting ? "not-allowed" : "pointer", background: "#fff", color: "#374151" }}
            onMouseEnter={(e) => { if (!deleting) e.currentTarget.style.background = "#f8fafc"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={deleting}
            style={{ padding: "9px 20px", border: "none", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: deleting ? "not-allowed" : "pointer", background: deleting ? "#fca5a5" : "#dc2626", color: "#fff", display: "flex", alignItems: "center", gap: 7, transition: "background .15s", opacity: deleting ? 0.8 : 1 }}>
            {deleting ? <><LoaderIcon size={14} /> Deleting…</> : <><TrashIcon /> Delete</>}
          </button>
        </div>
      </div>
    </div>
  );
};

const ViewDetailModal = ({ item, vendorProfileId, onClose, onEdit, onDeleted }) => {
  const [detail,  setDetail]  = useState(item || null);
  const [loading, setLoading] = useState(!item);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", onKey); };
  }, [onClose]);

  useEffect(() => {
    if (item) {
      setDetail(item);
      setLoading(false);
      return;
    }
    if (!detail?.id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await tcApi.get(vendorProfileId, detail?.id);
        if (!cancelled) setDetail(data?.data ?? data);
      } catch {
        if (!cancelled) toast.error("Failed to Load", "Could not fetch term details.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [item, detail?.id, vendorProfileId]);

  const catCfg = detail ? getCatConfig(detail.category) : null;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 600, boxShadow: "0 24px 64px rgba(0,0,0,.22)", animation: "tc-fadeUp .2s ease", display: "flex", flexDirection: "column", maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: catCfg ? `${catCfg.bg}` : "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", color: catCfg?.color || "#64748b" }}>
              <FileTextIcon />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: "#0f172a" }}>Term & Condition Details</h2>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {detail && (
              <button onClick={() => { onClose(); onEdit(detail); }}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#b45309", transition: "background .15s" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#fef08a")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#fef9c3")}>
                <EditIcon /> Edit
              </button>
            )}
            <button onClick={onClose}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 6, borderRadius: 6 }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#64748b")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}>
              <CloseIcon />
            </button>
          </div>
        </div>

        <div style={{ padding: "20px 24px 24px", overflowY: "auto", flex: 1 }}>
          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "20px 0" }}>
              {[1,2,3,4,5,6].map((i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <SkeletonBlock w={16} h={16} r={4} />
                  <SkeletonBlock w={120} h={12} />
                  <SkeletonBlock w="50%" h={12} />
                </div>
              ))}
            </div>
          ) : !detail ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "#dc2626", fontSize: 14 }}>
              Failed to load details. Please close and try again.
            </div>
          ) : (
            <>
              <div style={{ background: "linear-gradient(135deg, #f8fafc, #f1f5f9)", borderRadius: 10, padding: "16px 20px", marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: "0 0 4px", fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: ".08em", textTransform: "uppercase" }}>Title</p>
                  <p style={{ margin: 0, fontSize: 17, fontWeight: 800, color: "#0f1316", lineHeight: 1.4 }}>{detail.title}</p>
                </div>
                <ActiveBadge value={detail.is_active} />
              </div>

              <InfoRow icon={<TagIcon />}      label="Category"      badge={<CategoryBadge category={detail.category} label={detail.category_label} />} />
              <InfoRow icon={<FileTextIcon />} label="Short Text"    value={detail.short_text} />
              <InfoRow icon={<SlugIcon />}     label="Slug"          value={detail.slug} mono />
              <InfoRow icon={<UserIcon />}     label="Created By"    value={`${detail.created_by_email}`} />
              <InfoRow icon={<CalendarIcon />} label="Created At"    value={detail.created_at} />
              <InfoRow icon={<CalendarIcon />} label="Updated At"    value={detail.updated_at} />

              {detail.content && (
                <div style={{ marginTop: 18 }}>
                  <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em" }}>Full Content</p>
                  <div style={{ padding: "14px 18px", background: "#f8fafc", borderRadius: 9, border: "1px solid #e2e8f0", fontSize: 13.5, color: "#1e293b", lineHeight: 1.75 }}>
                    {detail.content}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const EMPTY_FORM = {
  title:      "",
  category:   "general",
  content:    "",
  short_text: "",
  sort_order: 0,
  is_active:  true,
};

const FormModal = ({ mode, initialData, vendorProfileId, onClose, onSuccess }) => {
  const isEdit = mode === "edit";

  const [form,   setForm]   = useState(isEdit ? { ...EMPTY_FORM, ...initialData } : EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const setField = (key, val) => {
    setForm((p) => ({ ...p, [key]: val }));
    setErrors((p) => ({ ...p, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim())      e.title      = "Title is required.";
    else if (form.title.trim().length < 5)  e.title = "Title must be at least 5 characters.";
    if (!form.content.trim())    e.content    = "Content is required.";
    if (!form.short_text.trim()) e.short_text = "Short text is required.";
    if (form.sort_order === "" || isNaN(Number(form.sort_order))) e.sort_order = "Enter a valid number.";
    return e;
  };

  const applyApiErrors = (apiErrors) => {
    const mapped = {};
    Object.entries(apiErrors).forEach(([key, msgs]) => {
      mapped[key] = Array.isArray(msgs) ? msgs[0] : msgs;
    });
    setErrors(mapped);
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      toast.error("Validation Failed", "Please fix the highlighted fields.");
      return;
    }

    setSaving(true);
    const loadId = toast.loading(isEdit ? "Updating term…" : "Creating term…");

    const payload = {
      title:      form.title.trim(),
      category:   form.category,
      content:    form.content.trim(),
      short_text: form.short_text.trim(),
      sort_order: parseInt(form.sort_order, 10) || 0,
      is_active:  form.is_active,
    };

    try {
      const { data } = isEdit
        ? await tcApi.patch(vendorProfileId, initialData.id, payload)
        : await tcApi.create(vendorProfileId, payload);

      toast.dismiss(loadId);
      toast.success(
        isEdit ? "Term Updated" : "Term Created",
        isEdit
          ? `"${form.title}" has been updated successfully.`
          : `"${form.title}" has been created successfully.`
      );
      onSuccess(data?.data ?? data);

    } catch (err) {
      toast.dismiss(loadId);
      const status = err?.response?.status;
      const body   = err?.response?.data;

      if (status === 400) {
        if (body && typeof body === "object" && !body.detail) {
          applyApiErrors(body);
          toast.error("Validation Failed", "Please fix the highlighted fields.");
        } else {
          toast.error("Request Failed", body?.detail || "Invalid request.");
        }
      } else if (status === 403) {
        toast.error("Access Denied", "You don't have permission to perform this action.");
      } else if (status === 404) {
        toast.error("Not Found", "Vendor profile not found.");
      } else if (!err.response) {
        toast.error("Network Error", "Unable to reach the server.");
      } else {
        toast.error("Server Error", `Unexpected error (HTTP ${status}).`);
      }
      setSaving(false);
    }
  };

  const ib = (key) => ({ ...inputBase(!!errors[key]) });
  const FieldError = ({ name }) =>
    errors[name] ? (
      <span style={{ fontSize: 11.5, color: "#dc2626", display: "flex", alignItems: "center", gap: 4, marginTop: 3 }}>
        <AlertTriangleIcon size={12} /> {errors[name]}
      </span>
    ) : null;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 580, boxShadow: "0 24px 64px rgba(0,0,0,.22)", animation: "tc-fadeUp .2s ease", display: "flex", flexDirection: "column", maxHeight: "92vh" }}
        onClick={(e) => e.stopPropagation()}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
                {isEdit ? "Edit Term & Condition" : "New Term & Condition"}
              </h2>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#94a3b8" }}>
                {isEdit ? `Editing: ${initialData?.title}` : "Fill in the details below."}
              </p>
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 6, borderRadius: 6 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#64748b")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}>
            <CloseIcon />
          </button>
        </div>

        <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>

          <div>
            <label style={{ fontSize: 12.5, fontWeight: 650, color: "#374151", display: "flex", gap: 3, marginBottom: 5 }}>
              Title <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              style={ib("title")}
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="e.g. Delivery Schedule Compliance"
              onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
              onBlur={(e)  => (e.target.style.borderColor = errors.title ? "#fca5a5" : "#e2e8f0")}
            />
            <FieldError name="title" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <label style={{ fontSize: 12.5, fontWeight: 650, color: "#374151", display: "block", marginBottom: 5 }}>Category <span style={{ color: "#dc2626" }}>*</span></label>
              <div style={{ position: "relative" }}>
                <select
                  value={form.category}
                  onChange={(e) => setField("category", e.target.value)}
                  style={{ ...ib("category"), padding: "9px 32px 9px 12px", appearance: "none", cursor: "pointer" }}>
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}><ChevronDownIcon /></span>
              </div>
              <FieldError name="category" />
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12.5, fontWeight: 650, color: "#374151", display: "flex", gap: 3, marginBottom: 5 }}>
              Short Text <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              style={ib("short_text")}
              value={form.short_text}
              onChange={(e) => setField("short_text", e.target.value)}
              placeholder="Brief one-liner summary"
              onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
              onBlur={(e)  => (e.target.style.borderColor = errors.short_text ? "#fca5a5" : "#e2e8f0")}
            />
            <FieldError name="short_text" />
          </div>

          <div>
            <label style={{ fontSize: 12.5, fontWeight: 650, color: "#374151", display: "flex", gap: 3, marginBottom: 5 }}>
              Full Content <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <textarea
              rows={5}
              style={{ ...ib("content"), resize: "vertical", lineHeight: 1.65 }}
              value={form.content}
              onChange={(e) => setField("content", e.target.value)}
              placeholder="Enter the full terms & conditions text…"
              onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
              onBlur={(e)  => (e.target.style.borderColor = errors.content ? "#fca5a5" : "#e2e8f0")}
            />
            <FieldError name="content" />
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "#f8fafc", borderRadius: 9, border: "1px solid #e2e8f0" }}>
            <div>
              <p style={{ margin: 0, fontSize: 13.5, fontWeight: 650, color: "#374151" }}>Active Status</p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#94a3b8" }}>Inactive terms won't appear in RFQs.</p>
            </div>
            <button type="button"
              onClick={() => setField("is_active", !form.is_active)}
              style={{ width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer", background: form.is_active ? "#22c55e" : "#e2e8f0", padding: 2, transition: "background .2s", position: "relative", flexShrink: 0 }}>
              <span style={{ display: "block", width: 20, height: 20, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.2)", transition: "transform .2s", transform: form.is_active ? "translateX(20px)" : "translateX(0)" }} />
            </button>
          </div>
        </div>

        <div style={{ padding: "14px 24px 20px", borderTop: "1px solid #f1f5f9", display: "flex", gap: 10, justifyContent: "flex-end", flexShrink: 0 }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: "9px 22px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", background: "#fff", color: "#374151" }}
            onMouseEnter={(e) => { if (!saving) e.currentTarget.style.background = "#f8fafc"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ padding: "9px 22px", border: "none", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", background: saving ? "#93c5fd" : (isEdit ? "#f59e0b" : "#3b82f6"), color: "#fff", display: "flex", alignItems: "center", gap: 7, transition: "background .15s" }}
            onMouseEnter={(e) => { if (!saving) e.currentTarget.style.background = isEdit ? "#d97706" : "#2563eb"; }}
            onMouseLeave={(e) => { if (!saving) e.currentTarget.style.background = isEdit ? "#f59e0b" : "#3b82f6"; }}>
            {saving
              ? <><LoaderIcon size={14} /> {isEdit ? "Updating…" : "Creating…"}</>
              : isEdit
              ? <><EditIcon /> Update Term</>
              : <><PlusIcon /> Create Term</>}
          </button>
        </div>
      </div>
    </div>
  );
};

const TermCard = ({ term, onView, onEdit, onDelete }) => {
  const catCfg = getCatConfig(term.category);
  return (
    <div
      className="tc-card"
      onClick={() => onView(term)}
      style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 18px", cursor: "pointer", display: "flex", flexDirection: "column", gap: 10, transition: "box-shadow .15s, border-color .15s", boxShadow: "0 1px 3px rgba(0,0,0,.04)", position: "relative", overflow: "hidden" }}>

      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: catCfg.color, borderRadius: "12px 12px 0 0" }} />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, paddingTop: 2 }}>
        <CategoryBadge category={term.category} label={term.category_label} />
        <ActiveBadge value={term.is_active} />
      </div>

      <div>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#0f172a", lineHeight: 1.4 }}>{term.title}</h3>
        {term.short_text && (
          <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "#64748b", lineHeight: 1.5 }}>{term.short_text}</p>
        )}
      </div>

      {term.content && (
        <p style={{ margin: 0, fontSize: 12.5, color: "#94a3b8", lineHeight: 1.6, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {term.content}
        </p>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto", paddingTop: 8, borderTop: "1px solid #f1f5f9" }}>
        <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <button title="View Details" onClick={() => onView(term)}
            className="tc-action-btn"
            style={{ display: "flex", alignItems: "center", padding: "5px 8px", gap: 4, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, cursor: "pointer", color: "#3b82f6", fontSize: 12, fontWeight: 600, transition: "opacity .15s" }}>
            <EyeIcon />
          </button>
          <button title="Edit" onClick={() => onEdit(term)}
            className="tc-action-btn"
            style={{ display: "flex", alignItems: "center", padding: "5px 8px", gap: 4, background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 6, cursor: "pointer", color: "#b45309", fontSize: 12, fontWeight: 600, transition: "opacity .15s" }}>
            <EditIcon />
          </button>
          <button title="Delete" onClick={() => onDelete(term)}
            className="tc-action-btn"
            style={{ display: "flex", alignItems: "center", padding: "5px 8px", gap: 4, background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 6, cursor: "pointer", color: "#dc2626", fontSize: 12, fontWeight: 600, transition: "opacity .15s" }}>
            <TrashIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

const SkeletonCard = () => (
  <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 12, overflow: "hidden", position: "relative" }}>
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "#f1f5f9" }} />
    <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 2 }}>
      <SkeletonBlock w={70} h={22} r={20} />
      <SkeletonBlock w={60} h={22} r={20} />
    </div>
    <div>
      <SkeletonBlock w="85%" h={15} />
      <div style={{ marginTop: 6 }}><SkeletonBlock w="60%" h={12} /></div>
    </div>
    <SkeletonBlock w="100%" h={12} />
    <SkeletonBlock w="80%" h={12} />
    <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 4, borderTop: "1px solid #f1f5f9" }}>
      <SkeletonBlock w={80} h={12} />
      <div style={{ display: "flex", gap: 6 }}>
        <SkeletonBlock w={32} h={28} r={6} />
        <SkeletonBlock w={32} h={28} r={6} />
        <SkeletonBlock w={32} h={28} r={6} />
      </div>
    </div>
  </div>
);

const StatsBar = ({ items, loading }) => {
  const total    = items.length;
  const active   = items.filter((i) => i.is_active).length;
  const inactive = total - active;
  const catCounts = items.reduce((acc, i) => {
    acc[i.category] = (acc[i.category] || 0) + 1;
    return acc;
  }, {});
  const topCat = Object.entries(catCounts).sort((a, b) => b[1] - a[1])[0];

  const StatCard = ({ label, value, color, bg, border }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderRadius: 10, background: bg, border: `1px solid ${border}`, flex: "1 1 110px", minWidth: 0 }}>
      <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>
          {loading ? <SkeletonBlock w={28} h={18} /> : value}
        </div>
        <div style={{ fontSize: 11.5, color: "#64748b", fontWeight: 550, marginTop: 2, letterSpacing: ".01em" }}>{label}</div>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
      <StatCard label="Total Terms" value={total}    color="#6366f1" bg="#f5f3ff" border="#e0e7ff" />
      <StatCard label="Active"      value={active}   color="#16a34a" bg="#f0fdf4" border="#bbf7d0" />
      <StatCard label="Inactive"    value={inactive} color="#dc2626" bg="#fff5f5" border="#fecaca" />
      {topCat && !loading && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderRadius: 10, background: `${getCatConfig(topCat[0]).bg}`, border: `1px solid ${getCatConfig(topCat[0]).border}`, flex: "1 1 130px", minWidth: 0 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: getCatConfig(topCat[0]).color, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>{topCat[1]}</div>
            <div style={{ fontSize: 11.5, color: "#64748b", fontWeight: 550, marginTop: 2 }}>Most: {getCatConfig(topCat[0]).label}</div>
          </div>
        </div>
      )}
    </div>
  );
};

const TermsConditions = ({ vendorProfileId }) => {
  const [allItems,      setAllItems]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [vendorError,   setVendorError]   = useState(null);
  const [search,        setSearch]        = useState("");
  const [filterCat,     setFilterCat]     = useState("");
  const [filterActive,  setFilterActive]  = useState("");
  const [currentPage,   setCurrentPage]   = useState(1);

  const [viewItem,    setViewItem]    = useState(null);
  const [editItem,    setEditItem]    = useState(null);
  const [deleteItem,  setDeleteItem]  = useState(null);
  const [showCreate,  setShowCreate]  = useState(false);
  const [deleting,    setDeleting]    = useState(false);

  const searchDebounce = useRef(null);

  const fetchItems = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setVendorError(null);
    try {
      const { data } = await tcApi.list(vendorProfileId, {
        search:   search,
        category: filterCat,
        isActive: filterActive,
      });
      setAllItems(data?.results ?? []);
      if (silent) toast.success("Refreshed", "Terms list updated.");
    } catch (err) {
      const status = err?.response?.status;
      const msg    = err?.response?.data?.detail;
      if (status === 403) {
        toast.error("Access Denied", "You don't have permission to view these terms.");
      } else if (status === 404) {
        setVendorError(msg || "Vendor profile not found.");
        setAllItems([]);
      } else if (!err.response) {
        toast.error("Network Error", "Unable to reach the server.");
      } else {
        toast.error("Failed to Load", msg || `HTTP ${status}`);
      }
      if (silent) toast.error("Refresh Failed", "Could not reload terms.");
    } finally {
      setLoading(false);
    }
  }, [vendorProfileId, search, filterCat, filterActive]);

  useEffect(() => { setCurrentPage(1); }, [search, filterCat, filterActive]);

  useEffect(() => {
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => fetchItems(), 350);
    return () => clearTimeout(searchDebounce.current);
  }, [fetchItems]);

  const totalCount = allItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pagedItems = useMemo(
    () => allItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [allItems, currentPage]
  );

  const hasFilters   = search || filterCat || filterActive !== "";
  const clearFilters = () => { setSearch(""); setFilterCat(""); setFilterActive(""); };

  const handleCreateSuccess = (newItem) => {
    setShowCreate(false);
    setAllItems((prev) => [newItem, ...prev]);
    setCurrentPage(1);
  };

  const handleEditSuccess = (updated) => {
    setEditItem(null);
    setAllItems((prev) => prev.map((it) => (it.id === updated.id ? { ...it, ...updated } : it)));
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    const loadId = toast.loading("Deleting term…");
    try {
      await tcApi.delete(vendorProfileId, deleteItem.id);
      toast.dismiss(loadId);
      toast.success("Term Deleted", `"${deleteItem.title}" has been removed successfully.`);
      setAllItems((prev) => prev.filter((it) => it.id !== deleteItem.id));
      setDeleteItem(null);
      const newTotal = totalCount - 1;
      const newPages = Math.ceil(newTotal / PAGE_SIZE);
      if (currentPage > newPages && newPages > 0) setCurrentPage(newPages);
    } catch (err) {
      toast.dismiss(loadId);
      const status = err?.response?.status;
      if (status === 403) {
        toast.error("Access Denied", "You don't have permission to delete this term.");
      } else if (status === 404) {
        toast.error("Not Found", "This term no longer exists.");
        setAllItems((prev) => prev.filter((it) => it.id !== deleteItem.id));
        setDeleteItem(null);
      } else if (!err.response) {
        toast.error("Network Error", "Unable to reach the server.");
      } else {
        toast.error("Delete Failed", `Unexpected error (HTTP ${status}).`);
      }
    } finally {
      setDeleting(false);
    }
  };

  const filterInputBase = {
    padding: "8px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 13.5,
    background: "#fff",
    color: "#1e293b",
    outline: "none",
    fontFamily: "inherit",
    transition: "border-color .15s",
  };

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", color: "#1e293b" }}>
      <style>{`
        @keyframes tc-shimmer   { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes tc-fadeUp    { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
        @keyframes tc-alIn      { from{opacity:0;transform:translateX(24px) scale(.97)} to{opacity:1;transform:none} }
        @keyframes tc-alOut     { from{opacity:1} to{opacity:0;transform:translateX(28px) scale(.95)} }
        @keyframes tc-spin      { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes tc-cardIn    { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
        .tc-card:hover          { box-shadow:0 6px 24px rgba(0,0,0,.09)!important; border-color:#cbd5e1!important; }
        .tc-action-btn:hover    { opacity:.8!important; }
        * { box-sizing:border-box; }
      `}</style>

      <ToastContainer />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#0f172a", letterSpacing: "-.01em" }}>Terms &amp; Conditions</h2>
          <p style={{ margin: "3px 0 0", fontSize: 13, color: "#64748b" }}>Manage vendor-specific terms applied to RFQs</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => fetchItems(true)} disabled={loading}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#fff", color: "#374151", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", boxShadow: "0 1px 3px rgba(0,0,0,.05)", transition: "background .15s" }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = "#f1f5f9"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}>
            {loading ? <LoaderIcon size={15} /> : <RefreshIcon />} Refresh
          </button>
          <button onClick={() => setShowCreate(true)}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 18px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "background .15s", boxShadow: "0 1px 3px rgba(0,0,0,.12)" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#2563eb")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#3b82f6")}>
            <PlusIcon /> Add Term
          </button>
        </div>
      </div>

      {vendorError && !loading && (
        <div style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 12, padding: "22px 24px", display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 20 }}>
          <div style={{ color: "#dc2626", flexShrink: 0, marginTop: 2 }}><AlertTriangleIcon size={22} /></div>
          <div>
            <p style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700, color: "#dc2626" }}>Vendor Profile Not Found</p>
            <p style={{ margin: 0, fontSize: 13.5, color: "#64748b" }}>{vendorError}</p>
          </div>
        </div>
      )}

      {!vendorError && <StatsBar items={allItems} loading={loading} />}

      {!vendorError && (
        <div style={{ background: "#fff", borderRadius: 12, padding: "12px 16px", border: "1px solid #e2e8f0", marginBottom: 18, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", boxShadow: "0 1px 3px rgba(0,0,0,.04)" }}>
          <div style={{ position: "relative", flex: "1 1 200px", minWidth: 160 }}>
            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}>
              <SearchIcon />
            </span>
            <input
              style={{ ...filterInputBase, width: "100%", paddingLeft: 32 }}
              placeholder="Search terms…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
              onBlur={(e)  => (e.target.style.borderColor = "#e2e8f0")}
            />
          </div>

          <div style={{ position: "relative", flex: "0 1 160px", minWidth: 130 }}>
            <select
              value={filterCat}
              onChange={(e) => setFilterCat(e.target.value)}
              style={{ ...filterInputBase, width: "100%", padding: "8px 30px 8px 12px", appearance: "none", cursor: "pointer" }}>
              <option value="">All Categories</option>
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}><ChevronDownIcon /></span>
          </div>

          <div style={{ position: "relative", flex: "0 1 140px", minWidth: 120 }}>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              style={{ ...filterInputBase, width: "100%", padding: "8px 30px 8px 12px", appearance: "none", cursor: "pointer" }}>
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}><ChevronDownIcon /></span>
          </div>

          {hasFilters && (
            <button onClick={clearFilters}
              style={{ ...filterInputBase, background: "#fff5f5", color: "#dc2626", border: "1px solid #fecaca", cursor: "pointer", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", padding: "8px 14px", flex: "0 0 auto" }}>
              Clear
            </button>
          )}
        </div>
      )}

      {!vendorError && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16, animation: "tc-fadeUp .25s ease" }}>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
              : pagedItems.length === 0
              ? null
              : pagedItems.map((term, idx) => (
                  <div key={term.id} style={{ animation: `tc-cardIn .25s ease ${(idx % PAGE_SIZE) * 0.03}s both` }}>
                    <TermCard
                      term={term}
                      onView={(t) => setViewItem(t)}
                      onEdit={(t) => setEditItem(t)}
                      onDelete={(t) => setDeleteItem(t)}
                    />
                  </div>
                ))
            }
          </div>

          {!loading && pagedItems.length === 0 && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 20px", background: "#fff", borderRadius: 12, border: "1px dashed #e2e8f0", gap: 12, textAlign: "center" }}>
              <div style={{ width: 64, height: 64, borderRadius: "50%", background: "linear-gradient(135deg,#f1f5f9,#e2e8f0)", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
                <FileTextIcon />
              </div>
              <div>
                <p style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#374151" }}>
                  {hasFilters ? "No terms match your filters" : "No terms & conditions yet"}
                </p>
                <p style={{ margin: 0, fontSize: 13.5, color: "#94a3b8" }}>
                  {hasFilters ? "Try adjusting your search or filters" : "Add the first term for this vendor."}
                </p>
              </div>
              {hasFilters ? (
                <button onClick={clearFilters}
                  style={{ marginTop: 4, padding: "9px 20px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                  Clear Filters
                </button>
              ) : (
                <button onClick={() => setShowCreate(true)}
                  style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6, padding: "9px 20px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                  <PlusIcon /> Add First Term
                </button>
              )}
            </div>
          )}

          {!loading && totalCount > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              onPageChange={(p) => { if (p >= 1 && p <= totalPages) setCurrentPage(p); }}
            />
          )}
        </>
      )}

      {showCreate && (
        <FormModal mode="create" initialData={null} vendorProfileId={vendorProfileId}
          onClose={() => setShowCreate(false)} onSuccess={handleCreateSuccess} />
      )}
      {editItem && (
        <FormModal mode="edit" initialData={editItem} vendorProfileId={vendorProfileId}
          onClose={() => setEditItem(null)} onSuccess={handleEditSuccess} />
      )}
      {viewItem && (
        <ViewDetailModal item={viewItem} vendorProfileId={vendorProfileId}
          onClose={() => setViewItem(null)} onEdit={(item) => setEditItem(item)}
          onDeleted={(id) => { setAllItems((prev) => prev.filter((it) => it.id !== id)); setViewItem(null); }} />
      )}
      {deleteItem && (
        <DeleteConfirmModal item={deleteItem} onConfirm={handleDelete}
          onCancel={() => !deleting && setDeleteItem(null)} deleting={deleting} />
      )}
    </div>
  );
};

export default TermsConditions;